import type { FC } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { AccountSelect } from "app/pages/sales-intelligence/modules/crm-export/components/AccountSelect/AccountSelect";
import { PlainTooltip } from "@similarweb/ui-components";
import type { ContactExportPreview } from "../../../../types/common";
import { selectCanCreateNewAccount } from "../../../../store/selectors";
import trackingService from "../../../../services/tracking/trackingService";
import { CONTACTS_EXPORT_PREVIEW_MODAL_CLASS } from "../consts";
import {
    StyledContactListItem,
    StyledContactListItemDisabled,
    StyledContactInfo,
    StyledContactName,
    StyledContactDetails,
    StyledIconButton,
    StyledCompanyName,
    StyledAlertIcon,
} from "./styles";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

type ContactExportPreviewItemProps = {
    contact: ContactExportPreview;
    integrationName: Integration;
    onChange(contact: ContactExportPreview, searchedLabel?: string): void;
    onDelete(contactId: string): void;
};

export const ContactExportPreviewItem: FC<ContactExportPreviewItemProps> = (props) => {
    const { contact, integrationName, onChange, onDelete } = props;
    const {
        firstName,
        lastName,
        accountName,
        accountId,
        contactId,
        jobTitle,
        isExistAsLead,
        matchedCompanies,
        searchedCompanyLabel,
    } = contact;

    const translate = useTranslation();
    const canCreateNewAccount = useSelector(selectCanCreateNewAccount);

    const handleAccountChange = (accountId: string, searchedLabel?: string) => {
        onChange({ ...contact, accountId }, searchedLabel);
    };

    const handleTrackSearch = () => trackingService.trackAccountSearch(integrationName);
    const handleTrackSelect = (isNewAccount: boolean) =>
        trackingService.trackAssignAccountToContact(integrationName, isNewAccount);

    const fullName = `${firstName} ${lastName}`;

    const accountNameMarkup = translate("si.advanced_search.results_section.contacts.company_at", {
        companyName: accountName,
    });

    const handleDelete = () => onDelete(contactId);

    const contactInfo = (
        <StyledContactInfo>
            <StyledContactName>
                {fullName}
                {isExistAsLead ? <StyledAlertIcon /> : null}
            </StyledContactName>
            <StyledContactDetails>
                {jobTitle}
                <StyledCompanyName dangerouslySetInnerHTML={{ __html: accountNameMarkup }} />
            </StyledContactDetails>
        </StyledContactInfo>
    );

    if (isExistAsLead)
        {return (
            <PlainTooltip
                tooltipContent={translate("si.advanced_search.contacts_export_preview_item.exist_as_lead.tooltip")}
                appendTo=".contacts-export-preview-modal-overlay"
            >
                <StyledContactListItemDisabled>
                    {contactInfo}
                    <AccountSelect
                        suggestedAccounts={matchedCompanies}
                        contactsCompanyName={accountName}
                        integrationName={integrationName}
                        onChange={handleAccountChange}
                        value={accountId}
                        canCreateNewAccount={canCreateNewAccount}
                        containerClassName={CONTACTS_EXPORT_PREVIEW_MODAL_CLASS}
                        onTrackSearch={handleTrackSearch}
                        onTrackSelect={handleTrackSelect}
                        existAsLead
                    />
                </StyledContactListItemDisabled>
            </PlainTooltip>
        );}

    return (
        <StyledContactListItem>
            {contactInfo}
            <AccountSelect
                suggestedAccounts={matchedCompanies}
                contactsCompanyName={accountName}
                integrationName={integrationName}
                onChange={handleAccountChange}
                value={accountId}
                canCreateNewAccount={canCreateNewAccount}
                containerClassName={CONTACTS_EXPORT_PREVIEW_MODAL_CLASS}
                onTrackSearch={handleTrackSearch}
                onTrackSelect={handleTrackSelect}
                searchedCompanyLabel={searchedCompanyLabel}
            />
            <StyledIconButton
                iconName="delete"
                type="flat"
                onClick={handleDelete}
            />
        </StyledContactListItem>
    );
};
